import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Building2, Loader2, RefreshCw } from 'lucide-react';
import { captureError } from '@/lib/sentry';
import { useEnterpriseCapabilities } from '@/hooks/useEnterpriseCapabilities';
import { useEnterpriseEmailMonitoring } from '@/hooks/useEnterpriseEmailMonitoring';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CampaignOverviewPanel } from '@/components/enterprise/CampaignOverviewPanel';
import { SenderHealthPanel } from '@/components/enterprise/SenderHealthPanel';
import { MemberQuotaPanel } from '@/components/enterprise/MemberQuotaPanel';
import {
  EnterpriseRecordDetailSheet,
  type EnterpriseDetailRecord,
} from '@/components/enterprise/EnterpriseRecordDetailSheet';

/**
 * /enterprise/email — M2 (#572) cross-workspace email monitoring dashboard.
 *
 * Visible only to users holding the `email_monitor` capability on at least one
 * enterprise partner. Self-gates on that capability (cosmetic — the server RPCs
 * recompute authz per partner and filter every row to the partner's workspaces).
 * Renders three panels (campaign overview, sender health, daily-quota) for the
 * selected partner. "View"/"Details" opens the row in an inline detail Sheet —
 * admins aren't members of the workspaces they monitor, so a cross-workspace
 * deep-link can't work (`switchWorkspace` silently no-ops); the Sheet renders
 * the already-loaded record instead.
 */
export function EnterpriseEmailMonitoring(): JSX.Element {
  const { partners, isLoading: capsLoading, hasCapability } = useEnterpriseCapabilities();

  const monitorPartners = useMemo(
    () => partners.filter((p) => hasCapability(p.partner_id, 'email_monitor')),
    [partners, hasCapability],
  );

  const [selectedPartnerId, setSelectedPartnerId] = useState<string | null>(null);
  const partnerId = selectedPartnerId ?? monitorPartners[0]?.partner_id ?? '';

  // The record shown in the inline detail Sheet (null = closed). Admins aren't
  // members of the workspaces they monitor, so we can't deep-link in — we show
  // the already-loaded record's detail here instead.
  const [detail, setDetail] = useState<EnterpriseDetailRecord | null>(null);

  const { campaigns, senders, quotas, isLoading, refetch, error } =
    useEnterpriseEmailMonitoring(partnerId);

  // Surface query failures to GlitchTip (per CLAUDE.md telemetry convention).
  useEffect(() => {
    if (error) {
      captureError(error as Error, { feature: 'enterprise-email-monitoring' });
    }
  }, [error]);

  // --- Gating (after hooks, to satisfy rules-of-hooks) ---------------------
  if (capsLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (monitorPartners.length === 0) {
    return <Navigate to="/" replace />;
  }

  const selectedPartner = monitorPartners.find((p) => p.partner_id === partnerId) ?? null;

  return (
    <div className="container mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Building2 className="h-5 w-5 text-foreground" />
            Email monitoring
          </h1>
          <p className="text-sm text-muted-foreground">
            Every campaign, sender account, and daily sending limit across the
            workspaces in
            {selectedPartner ? ` ${selectedPartner.name}` : ' your enterprise'}, with
            at-a-glance flags for campaigns that are erroring and senders that need
            reconnecting.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {monitorPartners.length > 1 && (
            <div className="w-56">
              <Select value={partnerId} onValueChange={(v) => setSelectedPartnerId(v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select enterprise" />
                </SelectTrigger>
                <SelectContent>
                  {monitorPartners.map((p) => (
                    <SelectItem key={p.partner_id} value={p.partner_id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`mr-2 h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <CampaignOverviewPanel
        campaigns={campaigns}
        partnerId={partnerId}
        onSelect={setDetail}
      />
      <SenderHealthPanel senders={senders} partnerId={partnerId} onSelect={setDetail} />
      <MemberQuotaPanel quotas={quotas} partnerId={partnerId} />

      <EnterpriseRecordDetailSheet
        record={detail}
        onOpenChange={(open) => {
          if (!open) setDetail(null);
        }}
      />
    </div>
  );
}

export default EnterpriseEmailMonitoring;
